import React, { useState, useEffect } from 'react';
import { ComposedChart, Scatter, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { apiFetch } from './api';
import { formatFeatureLabel, withFeatureLabels } from './featureLabels';
import './NormativeModelingView.css?v=4';

const NormativeModelingView = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter states
    const [selectedSex, setSelectedSex] = useState('All');
    const [selectedRegion, setSelectedRegion] = useState('Left-Hippocampus');
    const [selectedFeature, setSelectedFeature] = useState('bp_gamma');
    const [showCurve, setShowCurve] = useState(true);

    // Data states
    const [populationData, setPopulationData] = useState([]);
    const [trendData, setTrendData] = useState([]);
    const [regions, setRegions] = useState([]);
    const [features, setFeatures] = useState([]);

    // Fetch available regions and features on mount
    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const [regionsRes, featuresRes] = await Promise.all([
                    apiFetch('/api/normative-regions'),
                    apiFetch('/api/normative-features')
                ]);

                if (regionsRes.ok) {
                    const regionsData = await regionsRes.json();
                    setRegions(regionsData);
                    // Snap to a valid option if the default isn't offered by the
                    // backend, so the view never silently queries a region that
                    // returns no rows.
                    if (regionsData.length) {
                        setSelectedRegion((prev) => (regionsData.some((r) => r.id === prev) ? prev : regionsData[0].id));
                    }
                }
                if (featuresRes.ok) {
                    const featuresData = await featuresRes.json();
                    setFeatures(withFeatureLabels(featuresData));
                    if (featuresData.length) {
                        setSelectedFeature((prev) => (featuresData.some((f) => f.id === prev) ? prev : featuresData[0].id));
                    }
                }
            } catch (err) {
                console.error("Error fetching metadata:", err);
            }
        };

        fetchMetadata();
    }, []);

    // Fetch population data when filters change
    useEffect(() => {
        const fetchData = async () => {
            if (!selectedRegion || !selectedFeature) return;

            setLoading(true);
            setError(null);
            try {
                let url = `/api/normative-population?region=${selectedRegion}&feature=${selectedFeature}`;
                if (selectedSex !== 'All') {
                    url += `&sex=${selectedSex}`;
                }

                const response = await apiFetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch population data');
                }

                const data = await response.json();
                setPopulationData(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedRegion, selectedFeature, selectedSex]);

    // Fetch trend curve data from backend when showCurve is enabled
    useEffect(() => {
        const fetchTrendData = async () => {
            if (!showCurve || !selectedRegion || !selectedFeature) {
                setTrendData([]);
                return;
            }

            try {
                let url = `/api/normative-trend?region=${selectedRegion}&feature=${selectedFeature}`;
                if (selectedSex !== 'All') {
                    url += `&sex=${selectedSex}`;
                }

                const response = await apiFetch(url);
                if (!response.ok) {
                    throw new Error('Failed to fetch trend data');
                }

                const data = await response.json();
                setTrendData(data);
            } catch (err) {
                console.error('Error fetching trend data:', err);
                setTrendData([]);
            }
        };

        fetchTrendData();
    }, [selectedRegion, selectedFeature, selectedSex, showCurve]);

    return (
        <div className="normative-view-container" style={{ backgroundColor: 'transparent', padding: 0 }}>

            {/* Filters */}
            <div className="normative-filters">
                <div className="filter-group">
                    <label className="filter-label">Select Sex</label>
                    <select
                        value={selectedSex}
                        onChange={(e) => setSelectedSex(e.target.value)}
                        className="filter-select w-small"
                    >
                        <option value="All">All</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label className="filter-label">Select Region</label>
                    <select
                        value={selectedRegion}
                        onChange={(e) => setSelectedRegion(e.target.value)}
                        className="filter-select w-medium"
                    >
                        {regions.map(r => (
                            <option key={r.id} value={r.id}>{r.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label className="filter-label">Select Feature</label>
                    <select
                        value={selectedFeature}
                        onChange={(e) => setSelectedFeature(e.target.value)}
                        className="filter-select w-medium"
                    >
                        {features.map(f => (
                            <option key={f.id} value={f.id} title={f.description}>{f.label}</option>
                        ))}
                    </select>
                </div>

                <div className="toggle-container">
                    <div className="toggle-switch">
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            id="trend-curve-toggle"
                            checked={showCurve}
                            onChange={() => setShowCurve(!showCurve)}
                        />
                        <label className="toggle-label" htmlFor="trend-curve-toggle"></label>
                    </div>
                    <span className="toggle-text">MFP Trend</span>
                </div>
            </div>

            {/* Chart */}
            <div className="chart-section">
                {loading && (
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                        <div>Loading data...</div>
                    </div>
                )}
                {error && (
                    <div className="error-overlay">
                        <div>Error: {error}</div>
                    </div>
                )}
                <div className="chart-wrapper" style={{ opacity: loading || error ? 0.3 : 1, pointerEvents: loading || error ? 'none' : 'auto' }}>
                    <div className="chart-header">
                        <h3 className="chart-title">
                            {formatFeatureLabel(selectedFeature)} in {selectedRegion}
                        </h3>
                    </div>

                    <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                            <XAxis
                                type="number"
                                dataKey="age"
                                name="Age"
                                unit=" yrs"
                                domain={['auto', 'auto']}
                                stroke="#ccc"
                                tick={{ fill: '#ccc' }}
                            />
                            <YAxis
                                type="number"
                                dataKey="value"
                                name="Value"
                                domain={['auto', 'auto']}
                                stroke="#ccc"
                                tick={{ fill: '#ccc' }}
                            />
                            <Tooltip
                                cursor={{ strokeDasharray: '3 3' }}
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        return (
                                            <div style={{
                                                backgroundColor: 'rgba(26, 26, 46, 0.95)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                borderRadius: '8px',
                                                color: '#fff',
                                                padding: '12px'
                                            }}>
                                                <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '8px', fontSize: '12px', margin: 0 }}>
                                                    {`Age: ${Math.round(label)}`}
                                                </p>
                                                {payload.map((entry, index) => {
                                                    if (entry.name === 'Age') return null;
                                                    return (
                                                        <p key={index} style={{ color: '#fff', fontSize: '14px', margin: '4px 0 0 0' }}>
                                                            {`${entry.name} : ${typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}`}
                                                        </p>
                                                    );
                                                })}
                                            </div>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Legend
                                wrapperStyle={{ paddingTop: '12px' }}
                                payload={[
                                    { value: 'Population', type: 'circle', id: 'Population', color: '#8884d8' },
                                    ...(trendData.length > 0 ? [{ value: 'MFP Trend', type: 'line', id: 'MFP Trend', color: '#ff7300' }] : [])
                                ]}
                            />
                            <Scatter name="Population" data={populationData} fill="#8884d8" fillOpacity={0.6} size={60} />
                            {trendData.length > 0 && (
                                <Line
                                    type="monotone"
                                    data={trendData}
                                    dataKey="trendValue"
                                    xDataKey="age"
                                    stroke="#ff7300"
                                    strokeWidth={3}
                                    dot={false}
                                    name="MFP Trend"
                                    isAnimationActive={false}
                                />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default NormativeModelingView;
